import { DomainLandSituationType } from "@porchpass/api-client";
import type { LandData } from "../types/applicationTypes";

const isBoolSet = (value: boolean | undefined): boolean => value !== undefined;

const isNumberSet = (value: number | undefined): boolean =>
  value !== undefined && value !== null;

const isStringSet = (value: string | undefined): boolean =>
  value !== undefined && value !== null && value !== "";

/**
 * Mirrors backend CreateLand*Req.HasMinimumData() — dealer-portal land
 * endpoints reject requests with no substantive fields ("no data provided").
 * Autosave should wait until at least one field would be sent.
 */
export function hasMinimumLandSaveData(land: LandData): boolean {
  const situation = land.landSituation;
  if (!situation) return false;

  switch (situation) {
    case DomainLandSituationType.LandSituationTypeFamily:
    case DomainLandSituationType.LandSituationReservation:
      return true;

    case DomainLandSituationType.LandSituationTypeAlreadyOwned:
      return (
        isBoolSet(land.useAsDownPayment) ||
        isNumberSet(land.downPaymentAmount) ||
        isNumberSet(land.hoaFeeAmount) ||
        isNumberSet(land.estimatedLandValue) ||
        (isStringSet(land.purchaseDateMonth) &&
          isStringSet(land.purchaseDateYear)) ||
        isNumberSet(land.acreage) ||
        isBoolSet(land.hasFrontagePublicRoad) ||
        isBoolSet(land.hasPavedRoad)
      );

    case DomainLandSituationType.LandSituationTypeCurrentlyFinanced:
      return (
        isBoolSet(land.isRefinance) ||
        isBoolSet(land.useAsDownPayment) ||
        isNumberSet(land.downPaymentAmount) ||
        isNumberSet(land.hoaFeeAmount) ||
        isStringSet(land.landlordName) ||
        isNumberSet(land.estimatedLandValue) ||
        isNumberSet(land.existingMortgageBalance) ||
        (isStringSet(land.purchaseDateMonth) &&
          isStringSet(land.purchaseDateYear)) ||
        isNumberSet(land.acreage) ||
        isBoolSet(land.hasFrontagePublicRoad) ||
        isBoolSet(land.hasPavedRoad) ||
        isNumberSet(land.monthlyLandPayment)
      );

    case DomainLandSituationType.LandSituationTypePurchasing:
      return (
        isNumberSet(land.hoaFeeAmount) ||
        isStringSet(land.landlordName) ||
        isNumberSet(land.acreage) ||
        isBoolSet(land.hasFrontagePublicRoad) ||
        isBoolSet(land.hasPavedRoad) ||
        isNumberSet(land.existingMortgageBalance)
      );

    case DomainLandSituationType.LandSituationTypeLeasing:
      return (
        isNumberSet(land.hoaFeeAmount) ||
        isStringSet(land.landlordName) ||
        isStringSet(land.leaseType) ||
        isNumberSet(land.monthlyLeasePayment) ||
        isStringSet(land.priceIncreaseExplanation)
      );

    default:
      return false;
  }
}
